/** @jsxImportSource @emotion/react */
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiPageTemplate,
} from "@inscopix/ideas-eui";
import { DataTableContent } from "./layout/DataTableContent";
import { DataTableHeader } from "./layout/DataTableHeader";
import { DataTableMenuBar } from "./layout/DataTableMenuBar";
import { DataTableQuickActionBar } from "./layout/DataTableQuickActionBar/DataTableQuickActionBar";
import { memo, useCallback, useState } from "react";
import { DataTableBottomBar } from "./layout/DataTableBottomBar";
import { DataTableTooltipProvider } from "./layout/DataTableTooltip";
import { DataTableDragDropProvider } from "./layout/DataTableDragDropProvider";
import { css } from "@emotion/react";
import {
  useSetCurrentPage,
  useTourStep,
} from "providers/TourProvider/TourProvider.hooks";
import { useDataTableContext } from "./store/DataTableProvider";
import { DataTableRowData } from "./layout/DataTable";
import { GridApi, SelectionChangedEvent } from "ag-grid-community";
import assert from "assert";
export interface PageProjectDataTableProps {
  tableKey: string;
}
export const PageProjectDataTable = memo(function PageProjectDataTable({
  tableKey,
}: PageProjectDataTableProps) {
  const selectedTable = useDataTableContext((s) => {
    return s.tables.find((table) => table.key === tableKey);
  });

  assert(selectedTable !== undefined);
  const selectedTableKind = selectedTable?.kind;

  const [gridApi, setGridApi] = useState<
    GridApi<DataTableRowData> | undefined
  >();

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  useSetCurrentPage(
    selectedTableKind === "data" ? "projectDataTable" : "projectAnalysisTable",
  );

  const { component: TourDataTableUploadFiles2, anchorId } = useTourStep(
    "TourDataTableUploadFiles",
    2,
  );

  const handleSelectionChanged = useCallback(
    (e: SelectionChangedEvent<DataTableRowData>) => {
      const selectedRowIds = e.api.getSelectedRows().map((row) => row.id);
      setSelectedRowIds(selectedRowIds);
    },
    [setSelectedRowIds],
  );

  return (
    <EuiPageTemplate
      css={css`
        height: 100%;
      `}
      mainProps={{
        css: css`
          overflow: auto;
          max-block-size: calc(100vh - var(--euiFixedHeadersOffset, 0));
        `,
      }}
      // prevents this from switching to responsive layout
      responsive={[]}
    >
      <DataTableDragDropProvider>
        <DataTableTooltipProvider>
          <EuiFlexGroup
            direction="column"
            gutterSize="none"
            style={{ height: "100%", overflow: "hidden" }}
          >
            <EuiFlexItem
              grow={false}
              style={{ paddingLeft: 10, paddingRight: 10 }}
            >
              <DataTableHeader tableKey={tableKey} />
            </EuiFlexItem>

            <EuiFlexItem
              grow={false}
              style={{ paddingLeft: 5, paddingRight: 5 }}
              id={anchorId}
            >
              <DataTableMenuBar
                selectedRowIds={selectedRowIds}
                gridApi={gridApi}
                tableKey={tableKey}
              />
              {TourDataTableUploadFiles2}
            </EuiFlexItem>

            <EuiFlexItem
              grow={false}
              style={{ paddingLeft: 5, paddingRight: 5 }}
            >
              <EuiHorizontalRule margin="none" />
              <DataTableQuickActionBar
                tableId={selectedTable.id}
                selectedRowIds={selectedRowIds}
                gridApi={gridApi}
              />
            </EuiFlexItem>
            <EuiFlexItem grow={false}></EuiFlexItem>
            <EuiFlexItem style={{ minHeight: 0 }}>
              <DataTableContent
                tableKey={tableKey}
                gridApi={gridApi}
                setGridApi={setGridApi}
                handleSelectionChanged={handleSelectionChanged}
              />
            </EuiFlexItem>
          </EuiFlexGroup>
          <DataTableBottomBar
            tableId={selectedTable.id}
            selectedRowIds={selectedRowIds}
            gridApi={gridApi}
          />
        </DataTableTooltipProvider>
      </DataTableDragDropProvider>
    </EuiPageTemplate>
  );
});
